import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { ColDef } from "ag-grid-community";
import { useMemo } from "react";
import { Button, Descriptions, Modal, Spin } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";

import BasicTable from "@/components/shared/BasicTable";
import type { TMSShippingLabelDraft, TMSShippingPackageItem } from "@/types";
import { DialogRef } from "@/types/common";
import { useGlobalContext } from "@/hooks/context";
import {
  getScanOrderShippingLabelDraft,
  generateScanOrderShippingLabels,
} from "@/apis/tms/order";
import Indicator from "@/components/shared/Indicator";
import classNames from "classnames";

function formatAddress(addr: {
  name?: string;
  street: string;
  city: string;
  state: string;
  zip: string;
}) {
  const lines = [
    addr.name,
    addr.street,
    [addr.city, addr.state, addr.zip].filter(Boolean).join(", "),
  ].filter(Boolean);
  return lines.join("\n");
}

interface ShippingLabelDraftDescriptionProps {
  draft: TMSShippingLabelDraft;
}

function ShippingLabelDraftDescription({
  draft,
}: ShippingLabelDraftDescriptionProps) {
  return (
    <Descriptions
      size="small"
      column={2}
      bordered
      labelStyle={{ paddingBlock: 2, paddingInline: 8 }}
      contentStyle={{ paddingBlock: 2, paddingInline: 8 }}
      items={[
        { label: "Customer", children: draft.customer, span: 1 },
        { label: "Carrier", children: draft.carrier, span: 1 },
        { label: "Channel code", children: draft.channelCode, span: 1 },
        { label: "Packages", children: draft.packageList.length, span: 1 },
        ...(draft.upsAccount != null
          ? [{ label: "UPS Account", children: draft.upsAccount, span: 1 }]
          : []),
        ...(draft.signatureType != null
          ? [
              {
                label: "Signature type",
                children: draft.signatureType,
                span: 1,
              },
            ]
          : []),
        {
          label: "Shipper",
          children: (
            <pre
              style={{
                margin: 0,
                whiteSpace: "pre-wrap",
                fontFamily: "inherit",
              }}
            >
              {formatAddress(draft.shipper)}
            </pre>
          ),
          span: 1,
        },
        {
          label: "Recipient",
          children: (
            <pre
              style={{
                margin: 0,
                whiteSpace: "pre-wrap",
                fontFamily: "inherit",
              }}
            >
              {formatAddress(draft.recipient)}
            </pre>
          ),
          span: 1,
        },
      ]}
    />
  );
}

type PackageRow = TMSShippingPackageItem & { __rowId: string };

interface ShippingLabelPackageListProps {
  className?: string;
  packageList: TMSShippingPackageItem[];
}

const packageColumns: ColDef<PackageRow>[] = [
  {
    headerName: "#",
    width: 120,
    valueGetter: (params) => (params.node?.rowIndex ?? 0) + 1,
    suppressMenu: true,
  },
  {
    headerName: "SKU",
    field: "sku",
    minWidth: 100,
    suppressMenu: true,
  },
  {
    headerName: "Product",
    field: "productName",
    minWidth: 120,
    flex: 1,
    suppressMenu: true,
  },
  {
    headerName: "Qty",
    field: "quantity",
    maxWidth: 60,
    minWidth: 60,
    suppressMenu: true,
    cellClass: "ag-right-aligned-cell",
  },
  {
    headerName: "Dimensions (L × W × H)",
    minWidth: 160,
    suppressMenu: true,
    valueGetter: (params) => {
      const d = params.data;
      if (!d) return "";
      return `${Number(d.length).toFixed(2)} × ${Number(d.width).toFixed(2)} × ${Number(d.height).toFixed(2)} IN`;
    },
  },
  {
    headerName: "Weight",
    maxWidth: 90,
    minWidth: 90,
    suppressMenu: true,
    valueGetter: (params) => {
      const d = params.data;
      return d ? `${Number(d.weight).toFixed(2)} LB` : "";
    },
  },
];

function ShippingLabelPackageList({
  className,
  packageList,
}: ShippingLabelPackageListProps) {
  const page = useMemo(() => {
    const items: PackageRow[] = packageList.map((p, i) => ({
      ...p,
      __rowId: String(i),
    }));
    return {
      items,
      total: items.length,
      page: 1,
      size: items.length,
      pages: 1,
      sizes: [10, 20, 50],
    };
  }, [packageList]);

  return (
    <div className={classNames(className)}>
      <BasicTable<PackageRow>
        className={classNames(
          "order-table h-[300px] bg-white global_inherit_no_top_radius",
          "order-table !rounded-md border-[1px] border-gray-200",
        )}
        loading={false}
        columns={packageColumns}
        page={page}
        getRowId={(row) => row.__rowId}
        enableRowDetail={false}
        enablePagination={false}
        showRefresh={false}
      />
    </div>
  );
}

const shippingLabelDraftQueryKey = (orderId: string) =>
  ["scan-order-shipping-label-draft", orderId] as const;

export interface UploadLabelsRef {
  getPdfData: () => {
    file: UploadFile["originFileObj"];
    pageCount: number;
  } | null;
}

interface OpenDialogProps {
  orderId: string;
}

interface CreateShippingLabelsDialogProps {
  className?: string;
  onCreated: () => void;
}

const CreateShippingLabelsDialog = forwardRef<
  DialogRef,
  CreateShippingLabelsDialogProps
>((props, ref) => {
  const { className, onCreated } = props;
  const { hintPrompt } = useGlobalContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [isGeneratingOpen, setIsGeneratingOpen] = useState(false);

  const {
    data: draft,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: shippingLabelDraftQueryKey(orderId ?? ""),
    queryFn: () => getScanOrderShippingLabelDraft(orderId!),
    enabled: !!orderId && isModalOpen,
  });

  useEffect(() => {
    if (isError && error) {
      hintPrompt(
        "error",
        "Failed to load draft",
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  }, [isError, error, hintPrompt]);

  const closeDialog = useCallback(() => {
    setIsModalOpen(false);
    setOrderId(null);
  }, []);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleGenerateLabels = useCallback(() => {
    if (!orderId) return;
    setIsGeneratingOpen(true);
    generateScanOrderShippingLabels(orderId)
      .then(() => {
        setIsGeneratingOpen(false);
        hintPrompt("success", "Labels generated successfully");
        closeDialog();
        onCreated?.();
      })
      .catch((err) => {
        setIsGeneratingOpen(false);
        hintPrompt(
          "error",
          "Failed to generate labels",
          err?.msg ?? "Unknown error",
        );
      });
  }, [orderId, hintPrompt, closeDialog, onCreated]);

  const openDialog = useCallback((props: unknown) => {
    const { orderId: id } = props as OpenDialogProps;
    setOrderId(id);
    setIsModalOpen(true);
  }, []);

  useImperativeHandle(ref, () => ({
    showModal: openDialog,
    hideModal: closeDialog,
  }));

  return (
    <>
      <Modal
        title={null}
        open={isGeneratingOpen}
        centered
        footer={null}
        closable={false}
        maskClosable={false}
      >
        <div className="flex flex-row items-center justify-center gap-4 py-4">
          <Spin size="large" />
          <span className="text-lg">
            Generating labels, please wait, this may take about 60 seconds...
          </span>
        </div>
      </Modal>
      <Modal
        className={className}
        title="Review Shipping Labels"
        open={isModalOpen}
        onCancel={closeDialog}
        footer={null}
        width="60%"
        loading={isFetching}
        maskClosable={false}
      >
        {draft && (
          <div>
            <Indicator label="Basic Info" />
            <ShippingLabelDraftDescription draft={draft} />
            <Indicator label="Packages" />
            <ShippingLabelPackageList packageList={draft.packageList} />
          </div>
        )}
        <div className="flex flex-row justify-end mt-2 gap-2">
          <Button
            icon={<ReloadOutlined />}
            loading={isFetching && !!draft}
            onClick={handleRefresh}
          >
            Refresh
          </Button>

          <Button
            type="primary"
            loading={isGeneratingOpen}
            onClick={handleGenerateLabels}
            disabled={!orderId}
          >
            Generate Labels
          </Button>

          <Button danger onClick={closeDialog}>
            Cancel
          </Button>
        </div>
      </Modal>
    </>
  );
});

CreateShippingLabelsDialog.displayName = "CreateShippingLabelsDialog";

export default CreateShippingLabelsDialog;
